<template>
    <div v-if="employee && Object.keys(employee).length" class="ctc-details">

        <!-- Main row: left column (identity + CTC tables) / right column (summary cards).
             The design lays these out as one two-column frame — 918px + 20px gutter +
             458px — with the right-hand cards forming a single continuous stack, so the
             page is built as one row rather than several stacked rows. -->
        <div class="row ctc-details-row">

            <!-- ── Left column ───────────────────────────────────────────────── -->
            <div class="col-lg-8 ctc-left-col">

                <!-- Employee identity -->
                <div class="ctc-employee-header">
                    <div class="ctc-avatar">
                        <img :src="avatarUrl" class="ctc-avatar-img" alt=""
                             @error="$event.target.src = $api_url + '/avatar/no-image.jpg'" />
                    </div>
                    <div class="ctc-employee-meta">
                        <h5 class="ctc-employee-name">{{ employee.name }}</h5>
                        <div class="ctc-employee-role" v-if="employeeRole">{{ employeeRole }}</div>
                        <div class="ctc-info-list">
                            <div class="ctc-info-row" v-if="employee.email">
                                <b-icon icon="envelope-fill" class="ctc-info-icon"></b-icon>
                                <span class="ctc-info-email">{{ employee.email }}</span>
                            </div>
                            <div class="ctc-info-row">
                                <b-icon icon="calendar3" class="ctc-info-icon"></b-icon>
                                <span class="ctc-info-label">Joining date</span>
                                <span class="ctc-info-sep">:</span>
                                <span class="ctc-info-value">{{ formatDate(employee.date_of_joining) }}</span>
                            </div>
                            <div class="ctc-info-row">
                                <b-icon icon="calendar-range" class="ctc-info-icon"></b-icon>
                                <span class="ctc-info-label">Period</span>
                                <span class="ctc-info-sep">:</span>
                                <span class="ctc-info-value">{{ reportPeriod }}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Employee CTC -->
                <div class="ctc-section">
                    <h6 class="ctc-section-heading">Employee CTC</h6>
                    <!-- Rendered by the shared <common-list> (DemoList.vue). That component
                         is used in 100+ places and must not be modified, so the CTC-specific
                         presentation is driven entirely through the data it is given:
                           - every column is an appended_column, which keeps default_column
                             empty and so avoids DemoList's built-in employee avatar on `name`;
                           - `status` carries the cost type, and `settlement_status` the row
                             kind (item/subtotal/total). DemoList's getValueClass() stamps
                             `status-label status-label-<value>` onto exactly those keys, which
                             is the hook the badge and subtotal styling below hangs off;
                           - the two money columns are handed over pre-formatted (with the ৳
                             sign, as the design shows) instead of using DemoList's
                             `type: 'amount'`, which would strip the symbol.
                         The row-kind column is presentational only and is hidden in CSS.
                         Sorting is switched off from the outside — see the CSS block. -->
                    <div class="ctc-common-list-wrap">
                        <common-list
                            :key="`ctc-breakdown-${ctcListKey}`"
                            :employees="ctcListRows"
                            :default_column="emptyColumns"
                            :appended_column="ctcListColumns"
                            :company_options="emptyColumns"
                            primary_key="row_id"
                            v-on:childToParent="onCtcListEvent"
                        ></common-list>
                    </div>
                </div>

                <!-- Other Perks Section (if present) -->
                <div class="ctc-section" v-if="projectionGroups.otherPerkItems.length">
                    <h6 class="ctc-section-heading">Other Perts</h6>
                    <div class="table-container-custom table-responsive">
                        <table class="table mb-0 ctc-projection-table">
                            <thead>
                                <tr class="page_header_background_color">
                                    <th class="text-white border-0 ctc-col-description">Descriptions</th>
                                    <th class="text-white border-0 ctc-col-cost-type">Cost Type</th>
                                    <th class="text-white border-0 ctc-col-fixed">Fixed</th>
                                    <th class="text-white border-0 ctc-col-actual">Actual</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, index) in projectionGroups.otherPerkItems" :key="`perk-${index}`">
                                    <td>{{ item.name }}</td>
                                    <td class="text-center">
                                        <span class="ctc-group-badge badge-ctc">{{ item.cost_type }}</span>
                                    </td>
                                    <td class="text-right">৳{{ formatCurrency(item.projected_amount) }}</td>
                                    <td class="text-right">৳{{ formatCurrency(item.actual_amount) }}</td>
                                </tr>
                                <tr class="ctc-total-row">
                                    <td colspan="2">Total</td>
                                    <td class="text-right">৳{{ formatCurrency(projectionGroups.otherPerkSums.projected) }}</td>
                                    <td class="text-right">৳{{ formatCurrency(projectionGroups.otherPerkSums.actual) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <!-- ── Right column ──────────────────────────────────────────────── -->
            <div class="col-lg-4 ctc-right-col">

                <!-- CTC Summary -->
                <div class="ctc-card ctc-summary-card">
                    <div class="ctc-card-title">CTC Summary</div>
                    <div class="ctc-summary-amount">৳&nbsp;{{ formatCurrency(totalActualPayment) }}</div>
                    <div class="ctc-summary-caption">Overall payment and projection comparison</div>
                    <table class="table mb-0 ctc-summary-table">
                        <tbody>
                            <tr>
                                <td>Fixed CTC</td>
                                <td class="text-right">৳{{ formatCurrency(totalProjection) }}</td>
                            </tr>
                            <tr>
                                <td>Actual CTC</td>
                                <td class="text-right">৳{{ formatCurrency(totalActualProrated) }}</td>
                            </tr>
                            <tr>
                                <td>Paid Amount</td>
                                <td class="text-right">৳{{ formatCurrency(totalActualPayment) }}</td>
                            </tr>
                            <tr>
                                <td>Variance</td>
                                <td class="text-right" :class="variance > 0 ? 'text-danger' : 'text-success'">৳{{ formatCurrency(Math.abs(variance)) }}</td>
                            </tr>
                            <tr>
                                <td :class="variance > 0 ? 'text-danger' : 'text-success'">{{ variancePercentage }}%</td>
                                <td></td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Payment Summary -->
                <div class="ctc-card">
                    <div class="ctc-card-title">Payment Summary</div>
                    <div class="ctc-card-subtitle">CTC Pay</div>
                    <div class="ctc-payment-list" v-if="actualRows.length">
                        <div v-for="row in actualRows" :key="`snapshot-${row.payrun_id}`" class="ctc-payment-row">
                            <span class="ctc-payment-label">{{ formatPayrunLabel(row) }}</span>
                            <span class="ctc-payment-amount">৳{{ formatCurrency(row.total_payment) }}</span>
                        </div>
                    </div>
                    <div v-else class="ctc-empty-note">No payment records found.</div>
                </div>

                <!-- Custom Attributes -->
                <div class="ctc-card" v-if="customAttributes.length">
                    <div class="ctc-card-title">Custom attributes</div>
                    <div class="custom-attr-grid">
                        <div v-for="attr in customAttributes" :key="attr.label" class="custom-attr-item">
                            <div class="custom-attr-label">{{ attr.label }}</div>
                            <div class="custom-attr-values">
                                <span v-for="opt in attr.selected_options" :key="opt" class="custom-attr-badge">{{ opt }}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Download Reports -->
                <div class="ctc-card ctc-download-card">
                    <div class="ctc-download-title">Download Reports</div>
                    <!-- Same two treatments as the report page header: Fixed Report
                         wears the "Generate Projection" style (plain .btn-secondary),
                         Actual Report the "Upload Payment Data" one. -->
                    <button class="btn btn-secondary ctc-report-btn" @click="openDownloadModal('fixed')">
                        <i class="fa fa-download"></i> Fixed Report
                    </button>
                    <button class="btn btn-primary custom-wizard-btn ctc-report-btn" @click="openDownloadModal('actual')">
                        <i class="fa fa-download"></i> Actual Report
                    </button>
                </div>
            </div>
        </div>

        <!-- Payrun-wise Actual Payment Breakdown -->
        <div class="row ctc-payrun-row">
            <div class="col-12">
                <h6 class="ctc-section-heading ctc-section-heading-strong">Payrun-wise Actual Payment Breakdown</h6>
                <div class="table-container-custom table-responsive">
                    <table class="table mb-0 ctc-projection-table ctc-payrun-table">
                        <thead>
                            <tr class="page_header_background_color">
                                <th class="text-white border-0">Payrun</th>
                                <th v-for="column in actualColumns" :key="column" class="text-white border-0">
                                    {{ column }}
                                </th>
                                <th class="text-white border-0">Actual</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-if="!actualRows.length">
                                <td :colspan="actualColumns.length + 2" class="text-center">No payment records found.</td>
                            </tr>
                            <tr v-for="row in actualRows" :key="row.payrun_id">
                                <td class="ctc-payrun-name">{{ row.payrun_name }}</td>
                                <td v-for="column in actualColumns" :key="`${row.payrun_id}-${column}`" class="text-right">
                                    ৳{{ formatCurrency(row.amounts[column] || 0) }}
                                </td>
                                <td class="text-right">৳{{ formatCurrency(row.total_payment) }}</td>
                            </tr>
                            <tr v-if="actualRows.length" class="ctc-subtotal-row">
                                <td>Total</td>
                                <td v-for="column in actualColumns" :key="`total-${column}`" class="text-right">
                                    ৳{{ formatCurrency(actualColumnTotals[column] || 0) }}
                                </td>
                                <td class="text-right">৳{{ formatCurrency(totalActualPayment) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- Format Selection Modal -->
        <transition name="fade">
            <div v-if="showFormatModal" class="custom-modal-backdrop" @click="closeDownloadModal">
                <div class="custom-modal-content card shadow-lg border-0 rounded-lg" @click.stop>
                    <div class="card-body p-4 text-center">
                        <div class="modal-icon mb-3">
                            <i class="fa fa-file-pdf-o text-danger mr-2" style="font-size: 24px;"></i>
                            <i class="fa fa-file-excel-o text-success" style="font-size: 24px;"></i>
                        </div>
                        <h6 class="font-weight-bold text-dark mb-2">Download Report</h6>
                        <p class="text-secondary small mb-3">
                            Download your <strong>{{ downloadType === 'fixed' ? 'Fixed' : 'Actual' }}</strong> report in:
                        </p>

                        <!-- Currency Selection Buttons -->
                        <div class="d-flex justify-content-center mb-3" style="gap: 8px;">
                            <button
                                v-for="curr in ['BDT', 'USD', 'CAD', 'AUD']"
                                :key="curr"
                                class="btn btn-sm font-weight-bold px-3 py-1"
                                :class="selectedCurrency === curr ? 'btn-primary' : 'btn-outline-secondary'"
                                style="border-radius: 6px;"
                                @click="selectCurrency(curr)"
                            >
                                {{ curr }}
                            </button>
                        </div>

                        <!-- Rate Input Box -->
                        <div v-if="selectedCurrency !== 'BDT'" class="form-group mb-3 text-left mx-auto" style="max-width: 260px;">
                            <label class="font-weight-bold text-dark small mb-1">Conversion Rate (1 {{ selectedCurrency }} = ? Taka)</label>
                            <input
                                type="text"
                                class="form-control form-control-sm"
                                :class="{'is-invalid': rateError}"
                                v-model="conversionRate"
                                placeholder="e.g., 127"
                                required
                            />
                            <div class="invalid-feedback" style="font-size: 10px; display: block;" v-if="rateError">Rate is required, must be a number, and greater than 0.</div>
                        </div>

                        <p class="text-secondary small mb-3">Select the format you would like to download:</p>
                        <div class="d-flex justify-content-center" style="gap: 12px;">
                            <button class="btn btn-secondary ctc-format-btn" @click="downloadReport('pdf')">
                                <i class="far fa-file-pdf"></i> PDF
                            </button>
                            <button class="btn btn-primary custom-wizard-btn ctc-format-btn" @click="downloadReport('excel')">
                                <i class="far fa-file-excel"></i> Excel
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </transition>

    </div>
</template>

<script>
export default {
    name: 'AnnualReportEmployeeDetails',
    props: {
        employee:                      { type: Object,  default: () => ({}) },
        fromMonth:                     { type: String,  default: '' },
        toMonth:                       { type: String,  default: '' },
        labels:                        { type: Object,  default: () => ({}) },
        breakdownKeys:                 { type: Array,   default: () => [] },
        salaryAsBreakDownOrGrossValue: { type: Number,  default: 1 },
        decimalPointSettings:          { type: String,  default: 'no' },
    },
    data() {
        return {
            showFormatModal: false,
            downloadType: 'fixed',
            selectedCurrency: 'BDT',
            conversionRate: '',
            rateError: false,
            // Bumped when a different employee is opened so <common-list>, which
            // builds its column options in created(), re-initialises cleanly.
            ctcListKey: 0,
            // Shared frozen empty array — passing a fresh [] inline on every
            // render would re-trigger the list's watchers needlessly.
            emptyColumns: [],
        };
    },
    watch: {
        'employee.employee_id'() {
            this.ctcListKey++;
        }
    },
    computed: {
        // Same resolution order DemoList uses for its own avatars, so a missing or
        // placeholder picture falls back to the shared no-image asset.
        avatarUrl() {
            const base = this.$api_url + '/avatar/';
            const pic  = this.employee && this.employee.pic ? this.employee.pic : '';
            if (!pic || pic === 'demo.jpg' || pic === 'no-image.jpg') return base + 'no-image.jpg';
            return base + this.employee.company_id + '/' + pic;
        },
        // "HR Exec. | Human Resources" — designation and department on one line,
        // with the separator dropped when only one of the two is known.
        employeeRole() {
            return [this.employee.jobposition, this.employee.department]
                .filter(part => part && part !== '-')
                .join(' | ');
        },
        projectionGroups() {
            const employee = this.employee || {};
            const coreKeys = ['basic_salary', 'house_rent', 'medical_allowance', 'conveyance_allowance'];

            const isBreakdownKey = (key) => {
                const accessKey = key.replace('contract_', '');
                return this.breakdownKeys.includes(accessKey);
            };

            const isCore = (key) => {
                const fieldKey = key.replace('contract_', '');
                return coreKeys.includes(fieldKey) || fieldKey.startsWith('custom_gross_salary') || isBreakdownKey(key);
            };

            const grossSalaryItems = [];
            const grossPayFixedPayItems = [];
            const grossPayVariablePayItems = [];
            const otherPerkItems = [];

            Object.keys(employee).forEach(key => {
                if (key.startsWith('contract_actual_') || key.startsWith('ctc_actual_')) return;
                if (!key.startsWith('contract_') && !key.startsWith('ctc_')) return;

                const value     = Number(employee[key] || 0);
                const actualKey = key.startsWith('contract_')
                    ? key.replace('contract_', 'contract_actual_')
                    : key.replace('ctc_', 'ctc_actual_');
                const actualValue = Number(employee[actualKey] || 0);

                if (!value && !actualValue) return;

                if (key.startsWith('contract_')) {
                    if (key === 'contract_gross_salary') {
                        return;
                    }
                    if (this.salaryAsBreakDownOrGrossValue == 0) {
                        if (isBreakdownKey(key)) {
                            return;
                        }
                    }

                    const item = {
                        name: this.formatItemName(key),
                        cost_type: 'Fixed pay',
                        projected_amount: value,
                        actual_amount: actualValue,
                    };

                    if (isCore(key)) {
                        grossSalaryItems.push(item);
                    } else {
                        grossPayFixedPayItems.push(item);
                    }
                } else if (key.startsWith('ctc_')) {
                    const headMeta = (this.labels && this.labels.ctc_heads && this.labels.ctc_heads[key]) || {};
                    const category = headMeta.category || 'main_part';
                    const name = headMeta.name || this.formatItemName(key);

                    const item = {
                        name: name,
                        cost_type: 'Variable pay',
                        projected_amount: value,
                        actual_amount: actualValue,
                    };

                    if (category === 'main_part') {
                        grossPayVariablePayItems.push(item);
                    } else {
                        otherPerkItems.push(item);
                    }
                }
            });

            if (this.salaryAsBreakDownOrGrossValue == 0) {
                const grossProjected = Number(employee['contract_gross_salary'] || 0);
                const grossActual    = Number(employee['contract_actual_gross_salary'] || 0);
                if (grossProjected || grossActual) {
                    grossSalaryItems.unshift({
                        name: 'Gross Salary',
                        cost_type: 'Fixed pay',
                        projected_amount: grossProjected,
                        actual_amount: grossActual,
                    });
                }
            }

            const getSums = (items) => {
                let proj = 0; let act = 0;
                items.forEach(i => {
                    proj += i.projected_amount;
                    act += i.actual_amount;
                });
                return { projected: proj, actual: act };
            };

            const grossSalarySums = getSums(grossSalaryItems);
            const grossPayFixedPaySums = getSums(grossPayFixedPayItems);
            const grossPayVariablePaySums = getSums(grossPayVariablePayItems);
            const otherPerkSums = getSums(otherPerkItems);

            const totalCtcSums = {
                projected: grossSalarySums.projected + grossPayFixedPaySums.projected + grossPayVariablePaySums.projected,
                actual: grossSalarySums.actual + grossPayFixedPaySums.actual + grossPayVariablePaySums.actual,
            };

            return {
                grossSalaryItems,
                grossSalarySums,
                grossPayFixedPayItems,
                grossPayFixedPaySums,
                grossPayVariablePayItems,
                grossPayVariablePaySums,
                totalCtcSums,
                otherPerkItems,
                otherPerkSums,
            };
        },
        // Column set handed to <common-list>. Everything lives in appended_column
        // (never default_column) so DemoList renders plain cells instead of its
        // avatar-prefixed employee name cell. The money columns are plain text —
        // they arrive already formatted with the ৳ sign — and are right-aligned
        // from the CSS below.
        ctcListColumns() {
            return [
                { value: 'description',       text: 'Descriptions', type: 'text' },
                { value: 'status',            text: 'Cost Type',    type: 'text' },
                { value: 'projected',         text: 'Fixed',        type: 'text' },
                { value: 'actual',            text: 'Actual',       type: 'text' },
                // Presentational hook only — hidden via CSS. See template note.
                { value: 'settlement_status', text: 'Row Type',     type: 'text' },
            ];
        },
        // Flattens the grouped projection into the flat row array DemoList wants,
        // keeping the design's running order: items, then the group subtotal,
        // then finally the overall CTC total.
        ctcListRows() {
            const groups = this.projectionGroups;
            const rows   = [];
            let rowId    = 0;

            const addItem = (item) => {
                rows.push({
                    row_id:            ++rowId,
                    description:       item.name,
                    status:            item.cost_type,
                    projected:         this.formatAmount(item.projected_amount),
                    actual:            this.formatAmount(item.actual_amount),
                    settlement_status: 'item',
                });
            };

            const addSummary = (label, sums, kind) => {
                rows.push({
                    row_id:            ++rowId,
                    description:       label,
                    status:            '',
                    projected:         this.formatAmount(sums.projected),
                    actual:            this.formatAmount(sums.actual),
                    settlement_status: kind,
                });
            };

            groups.grossSalaryItems.forEach(item => {
                if (item.name !== 'Gross Salary') addItem(item);
            });
            if (groups.grossSalaryItems.length) {
                addSummary('Gross Salary', groups.grossSalarySums, 'subtotal');
            }

            groups.grossPayFixedPayItems.forEach(addItem);
            if (groups.grossPayFixedPayItems.length) {
                addSummary('Gross pay/fixed pay', groups.grossPayFixedPaySums, 'subtotal');
            }

            groups.grossPayVariablePayItems.forEach(addItem);
            if (groups.grossPayVariablePayItems.length) {
                addSummary('Gross pay/Variable pay', groups.grossPayVariablePaySums, 'subtotal');
            }

            if (rows.length) {
                addSummary('Total Cost to the Company(CTC)', groups.totalCtcSums, 'total');
            }

            return rows;
        },
        actualDetail() {
            return this.employee && this.employee.actual_detail ? this.employee.actual_detail : null;
        },
        actualPayruns() {
            return this.actualDetail && this.actualDetail.payruns ? this.actualDetail.payruns : [];
        },
        customAttributes() {
            return this.actualDetail && this.actualDetail.custom_attributes ? this.actualDetail.custom_attributes : [];
        },
        actualColumns() {
            const titles = [];
            this.actualPayruns.forEach(payrun => {
                (payrun.items || []).forEach(item => {
                    if (!titles.includes(item.title)) {
                        titles.push(item.title);
                    }
                });
            });
            return titles;
        },
        actualRows() {
            return this.actualPayruns.map(payrun => {
                const amounts = {};
                this.actualColumns.forEach(column => {
                    amounts[column] = 0;
                });
                (payrun.items || []).forEach(item => {
                    if (!amounts[item.title]) {
                        amounts[item.title] = 0;
                    }
                    amounts[item.title] += Number(item.amount || 0);
                });
                return {
                    payrun_id: payrun.payrun_id,
                    payrun_name: payrun.payrun_name,
                    run_type: payrun.run_type,
                    check_date: payrun.check_date,
                    amounts,
                    total_payment: Number(payrun.total_payment || 0),
                    is_ctc_payment: !!payrun.is_ctc_payment
                };
            });
        },
        actualColumnTotals() {
            const totals = {};
            this.actualColumns.forEach(column => { totals[column] = 0; });
            this.actualRows.forEach(row => {
                this.actualColumns.forEach(column => {
                    totals[column] += Number(row.amounts[column] || 0);
                });
            });
            return totals;
        },
        totalProjection() {
            return Number(this.employee && this.employee.total_ctc_projection ? this.employee.total_ctc_projection : 0);
        },
        totalActualProrated() {
            return Number(this.employee && this.employee.total_ctc_actual ? this.employee.total_ctc_actual : 0);
        },
        totalActualPayment() {
            if (this.actualDetail && this.actualDetail.grand_total_payment) {
                return Number(this.actualDetail.grand_total_payment);
            }
            return Number(this.employee && this.employee.total_paid_amount ? this.employee.total_paid_amount : 0);
        },
        variance() {
            return this.totalActualProrated - this.totalActualPayment;
        },
        variancePercentage() {
            if (this.totalActualProrated === 0) return 0;
            return ((this.variance / this.totalActualProrated) * 100).toFixed(1);
        },
        reportPeriod() {
            if (!this.fromMonth || !this.toMonth) return '';
            return `${this.formatMonthLabel(this.fromMonth)} - ${this.formatMonthLabel(this.toMonth)}`;
        }
    },
    methods: {
        // <common-list> emits on row click / selection. The CTC breakdown rows are
        // read-only, so the event is accepted and deliberately ignored — without a
        // handler Vue would warn about the unhandled emit.
        onCtcListEvent() {},
        formatDate(value) {
            if (!value || value === '-') return '-';
            const cleanVal = String(value).trim();
            if (cleanVal === '0000-00-00' || cleanVal === '00-00-0000' || cleanVal.startsWith('0000-00-00') || cleanVal.startsWith('00-00-0000')) return '-';

            const parts = cleanVal.split('-');
            if (parts.length === 3) {
                const year = Number(parts[0]);
                const month = Number(parts[1]) - 1;
                const day = Number(parts[2]);
                const date = new Date(year, month, day);
                if (!isNaN(date.getTime())) {
                    const formattedDay = String(date.getDate()).padStart(2, '0');
                    const formattedMonth = date.toLocaleString('en-US', { month: 'long' });
                    const formattedYear = date.getFullYear();
                    return `${formattedDay} ${formattedMonth} ${formattedYear}`;
                }
            }
            const date = new Date(cleanVal);
            if (isNaN(date.getTime())) return value;
            const formattedDay = String(date.getDate()).padStart(2, '0');
            const formattedMonth = date.toLocaleString('en-US', { month: 'long' });
            const formattedYear = date.getFullYear();
            return `${formattedDay} ${formattedMonth} ${formattedYear}`;
        },
        formatCurrency(value) {
            const num = Number(value || 0);
            if (this.decimalPointSettings === 'yes') {
                return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            }
            return Math.round(num).toLocaleString('en-IN');
        },
        // Money as it is printed inside the <common-list> cells, which take plain
        // strings — the ৳ sign has to travel with the value.
        formatAmount(value) {
            return `৳${this.formatCurrency(value)}`;
        },
        // Payrun names arrive as "Payroll Run for January, 2026". The Payment
        // Summary shows the period inline next to the amount, so the boilerplate
        // prefix is stripped. Off-cycle runs (run_type 11) are tagged so they are
        // distinguishable from the main run for the same month —
        // e.g. "May, 2026 (Off-cycle)".
        formatPayrunLabel(row) {
            const raw = row && row.payrun_name ? String(row.payrun_name).trim() : '';
            if (!raw) return '-';

            // Payrun names are built server-side as "<descriptor> Payroll Run<time>
            // for <Month>, <Year>" — the descriptor and timestamp both vary, so the
            // period is taken as whatever follows " for ".
            const match = raw.match(/\bfor\s+(.+)$/i);
            const label = match ? match[1].trim() : raw;

            // run_type 11 = off-cycle. Tagging it keeps an off-cycle run
            // distinguishable from the main run for the same month.
            return Number(row.run_type) === 11 ? `${label} (Off-cycle)` : label;
        },
        formatItemName(key) {
            if (this.labels && this.labels.contract && this.labels.contract[key]) {
                return this.labels.contract[key];
            }
            const prefix = key.startsWith('contract_') ? 'contract_' : 'ctc_';
            return key
                .replace(prefix, '')
                .split('_')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
        },
        formatMonthLabel(value) {
            if (!value) return '';
            const [year, month] = value.split('-');
            const date = new Date(Number(year), Number(month) - 1, 1);
            return date.toLocaleString('en-US', { month: 'long', year: 'numeric' });
        },
        openDownloadModal(type) {
            this.downloadType = type;
            this.selectedCurrency = 'BDT';
            this.conversionRate = '';
            this.rateError = false;
            this.showFormatModal = true;
        },
        closeDownloadModal() {
            this.showFormatModal = false;
        },
        selectCurrency(curr) {
            this.selectedCurrency = curr;
            if (curr === 'BDT') {
                this.conversionRate = '';
                this.rateError = false;
            }
        },
        downloadReport(format) {
            if (this.selectedCurrency !== 'BDT') {
                const valueStr = String(this.conversionRate).trim();
                const numRegex = /^[0-9]+(\.[0-9]+)?$/;
                if (!numRegex.test(valueStr)) {
                    this.rateError = true;
                    return;
                }
                const rate = parseFloat(valueStr);
                if (isNaN(rate) || rate <= 0) {
                    this.rateError = true;
                    return;
                }
            }
            this.rateError = false;
            this.showFormatModal = false;
            const params = new URLSearchParams();
            params.append('employee_id', this.employee.employee_id);
            params.append('from_month', this.fromMonth);
            params.append('to_month', this.toMonth);
            params.append('report_type', this.downloadType);
            params.append('format', format);
            params.append('currency', this.selectedCurrency);
            if (this.selectedCurrency !== 'BDT' && this.conversionRate) {
                params.append('conversion_rate', this.conversionRate);
            }

            axios.get(`/cost-to-company/report/employee/export?${params.toString()}`, { responseType: 'blob' })
                .then(response => {
                    const ext = format === 'pdf' ? 'pdf' : 'xlsx';
                    const employeeNameSnake = (this.employee.name || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
                    const filename = `CTC_Report_${employeeNameSnake}_${this.downloadType.charAt(0).toUpperCase() + this.downloadType.slice(1)}_${this.fromMonth.split('-')[0]}.${ext}`;
                    const mimeType = format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
                    const url = window.URL.createObjectURL(new Blob([response.data], { type: mimeType }));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', filename);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                })
                .catch(error => {
                    console.error('Download error:', error);
                    if (this.$bvToast) {
                        this.$bvToast.toast('Error downloading report', {
                            title: 'Error',
                            variant: 'danger',
                            solid: true
                        });
                    } else {
                        alert('Error downloading report');
                    }
                });
        },
        async getSiteOptionSettings() {
            try {
                const response = await axios.get(this.$api_url + '/cost-to-company/get-report-options');
                const siteOptions = response.data.ctc_site_options || {};
                Object.values(siteOptions).forEach(option => {
                    if (option.opt_key === "ctc_salary_as_break_down_or_gross") {
                        this.salaryAsBreakDownOrGrossValue = option.status;
                    }
                });
            } catch (error) {
                console.error('Error fetching Site Option Setting: ', error);
            }
        },
    },
}
</script>

<style scoped>
/* ---------------------------------------------------------------------------
   Every size, weight and colour below is taken from the Figma CSS export of the
   "Employee CTC Details" frame (Frame 427319456) rather than eyeballed, so the
   values can be diffed against the export one line at a time.
   --------------------------------------------------------------------------- */
.ctc-details {
    font-family: 'Poppins', sans-serif;
    letter-spacing: -0.02em;
}

.page_header_color{
    color: #01524B;
}
.page_header_background_color{
    background-color: #01524B;
}

/* Left / right columns. The frame gutter is 20px, the vertical rhythm inside
   the left column is 46px (identity → first table) and 35px between tables;
   the right-hand card stack is spaced 38px apart. */
.ctc-details-row {
    margin-left: -10px;
    margin-right: -10px;
}

.ctc-left-col,
.ctc-right-col {
    padding-left: 10px;
    padding-right: 10px;
}

.ctc-right-col {
    display: flex;
    flex-direction: column;
    gap: 38px;
}

.ctc-section {
    margin-top: 35px;
}

.ctc-payrun-row {
    margin-top: 50px;
}

/* ── Employee identity ──────────────────────────────────────────────────── */
.ctc-employee-header {
    display: flex;
    align-items: flex-start;
    gap: 28px;
    margin-bottom: 46px;
}

.ctc-avatar {
    flex: 0 0 197px;
    width: 197px;
    height: 191px;
    border-radius: 12px;
    background-color: #CCEBED;
    overflow: hidden;
}

.ctc-avatar-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.ctc-employee-meta {
    padding-top: 10px;
    min-width: 0;
}

.ctc-employee-name {
    font-size: 20px;
    font-weight: 700;
    line-height: 30px;
    letter-spacing: -0.02em;
    color: #26565C;
    margin: 0;
}

.ctc-employee-role {
    font-size: 12px;
    font-weight: 500;
    line-height: 18px;
    letter-spacing: -0.02em;
    /* BW/50 */
    color: #8F8F8F;
}

.ctc-info-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 20px;
}

.ctc-info-row {
    display: flex;
    align-items: center;
    font-size: 15px;
    font-weight: 500;
    line-height: 22px;
    letter-spacing: -0.02em;
}

.ctc-info-icon {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    margin-right: 18px;
    /* Primary/80 */
    color: #01524B;
}

.ctc-info-email,
.ctc-info-label {
    /* BW/75 */
    color: #747474;
}

.ctc-info-label {
    width: 112px;
    flex-shrink: 0;
}

.ctc-info-sep {
    color: #747474;
    margin-right: 12px;
}

.ctc-info-value {
    color: #34444C;
}

/* ── Section headings ───────────────────────────────────────────────────── */
.ctc-section-heading {
    font-size: 18px;
    font-weight: 600;
    line-height: 27px;
    letter-spacing: -0.02em;
    color: #000000;
    text-align: left;
    margin: 0 0 6px 0;
}

/* The full-width payrun heading is the one heading the frame sets in bold. */
.ctc-section-heading-strong {
    font-weight: 700;
    margin-bottom: 13px;
}

/* ── Right-hand cards ───────────────────────────────────────────────────── */
.ctc-card {
    background-color: #FFFFFF;
    border: 1px solid #E8E8E8;
    border-radius: 16px;
    padding: 15px;
}

.ctc-card-title {
    font-size: 20px;
    font-weight: 700;
    line-height: 30px;
    letter-spacing: -0.02em;
    color: #8A8A8A;
}

.ctc-card-subtitle {
    font-size: 20px;
    font-weight: 600;
    line-height: 22px;
    letter-spacing: -0.02em;
    color: #01524B;
    margin-top: 4px;
}

/* CTC Summary */
.ctc-summary-amount {
    font-size: 30px;
    font-weight: 600;
    line-height: 30px;
    letter-spacing: -0.02em;
    color: #01524B;
    margin-top: 26px;
}

.ctc-summary-caption {
    font-size: 15px;
    font-weight: 500;
    line-height: 22px;
    letter-spacing: -0.02em;
    color: #34444C;
    margin-top: 3px;
}

.ctc-summary-table {
    margin-top: 12px;
    border-collapse: collapse;
}

.ctc-summary-table td {
    height: 41px;
    padding: 10px 12px;
    font-size: 14px;
    font-weight: 500;
    line-height: 21px;
    letter-spacing: -0.02em;
    color: #34444C;
    vertical-align: middle;
    border: 1px solid #dee2e6;
}

/* Alternating row contrast, matching the frame's 94%-white / white pairing */
.ctc-summary-table tbody tr:nth-child(odd) {
    background-color: #F3F3F3;
}

/* Payment Summary */
.ctc-payment-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 26px;
}

.ctc-payment-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 35px;
}

.ctc-payment-label,
.ctc-payment-amount {
    font-size: 15px;
    font-weight: 500;
    line-height: 33px;
    letter-spacing: -0.02em;
    color: #34444C;
}

.ctc-payment-amount {
    text-align: right;
    white-space: nowrap;
}

.ctc-empty-note {
    margin-top: 12px;
    font-size: 13px;
    color: #747474;
}

/* Custom attributes */
.custom-attr-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px 16px;
    margin-top: 12px;
}

.custom-attr-label {
    font-size: 12px;
    font-weight: 500;
    line-height: 22px;
    color: #34444C;
}

.custom-attr-values {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}

.custom-attr-badge {
    display: inline-flex;
    align-items: center;
    height: 22px;
    padding: 0 8.24px;
    border-radius: 3px;
    background-color: #F5F5F5;
    border: 0.82px solid #126D66;
    font-size: 12px;
    font-weight: 600;
    line-height: 8px;
    letter-spacing: -0.02em;
    color: #01524B;
}

/* Download Reports */
.ctc-download-card {
    padding: 20px 15px;
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.ctc-download-title {
    font-size: 20px;
    font-weight: 500;
    line-height: 30px;
    letter-spacing: -0.02em;
    text-align: center;
    color: #01524B;
}

/* Colour, radius, weight and padding come from the shared Employee Management
   button pair (.btn-secondary / .custom-wizard-btn, defined globally in
   resources/sass/atlhr.scss). Only the card-specific geometry lives here: the
   two report buttons fill the card width and centre their icon and label. */
.ctc-report-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 9px;
    width: 100%;
    margin: 0;
}

/* ── Plain tables (Other Perks, Payrun-wise breakdown) ──────────────────── */
.ctc-projection-table {
    margin-bottom: 0;
    border-radius: 4px 4px 0 0;
}

.ctc-projection-table thead th {
    height: 45px;
    padding: 10px !important;
    font-size: 16px;
    font-weight: 600;
    line-height: 14px;
    letter-spacing: -0.02em;
    text-align: center;
    vertical-align: middle;
}

/* Header dividers — the cells carry .border-0, so they have to be reinstated
   explicitly. The four-column tables only divide the Descriptions column off;
   the payrun table divides every column, as in the frame. */
.ctc-projection-table thead th:nth-child(2),
.ctc-payrun-table thead th + th {
    border-left: 1px solid #126D66 !important;
}

.ctc-projection-table tbody td {
    height: 40px;
    padding: 10px !important;
    font-size: 15px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: -0.02em;
    color: #34444C;
    border: 1px solid #E8E8E8;
    vertical-align: middle;
}

/* Column widths shared by the Employee CTC and Other Perks tables:
   310.92 / 236 / 184 / 187 of the 918px frame. */
.ctc-col-description { width: 33.87%; }
.ctc-col-cost-type   { width: 25.71%; }
.ctc-col-fixed       { width: 20.04%; }
.ctc-col-actual      { width: 20.38%; }

.ctc-projection-table tbody tr:not(.ctc-total-row):not(.ctc-subtotal-row):hover {
    background-color: rgba(0, 0, 0, 0.03);
}

.ctc-subtotal-row {
    background-color: #F8F8F8;
}

.ctc-subtotal-row td {
    font-weight: 600;
}

.ctc-total-row {
    background-color: #D3F5F2;
}

.ctc-total-row td {
    font-weight: 600;
}

/* Payrun table — two-line headings are expected here, hence the taller header,
   and the payrun name is the one body cell the design picks out in green. */
.ctc-payrun-table thead th {
    height: 57px;
    line-height: 24px;
}

.ctc-payrun-name {
    font-weight: 600 !important;
    color: #01524B !important;
}

/* ---------------------------------------------------------------------------
   <common-list> (DemoList.vue) presentation for the EMPLOYEE CTC breakdown.
   DemoList itself is shared by 100+ screens and is deliberately left untouched,
   so every rule here reaches into its markup from the outside via ::v-deep.
   --------------------------------------------------------------------------- */
.ctc-common-list-wrap {
    --ctc-green:           #01524B;
    --ctc-head-divider:    #126D66;
    --ctc-ink:             #34444C;
    --ctc-rule:            #E8E8E8;
    --ctc-subtotal-bg:     #F8F8F8;
    --ctc-total-bg:        #D3F5F2;
    --ctc-fixed-bg:        #FFF3E1;
    --ctc-fixed-border:    #E2C3B5;
    --ctc-fixed-text:      #9D5617;
    --ctc-variable-bg:     #E1F2FF;
    --ctc-variable-border: #B5CBE2;
    --ctc-variable-text:   #0060C9;

    border-radius: 4px 4px 0px 0px;
    overflow: hidden;
}

/* The breakdown in the design is a bare table: no search field, no column
   picker, no filter drawer, no record counter. Those controls belong to the
   full-page list screens, so they are suppressed here. */
.ctc-common-list-wrap ::v-deep .card-header,
.ctc-common-list-wrap ::v-deep .pagination,
.ctc-common-list-wrap ::v-deep .b-pagination-nav {
    display: none !important;
}

.ctc-common-list-wrap ::v-deep .demolistwrap2,
.ctc-common-list-wrap ::v-deep .card-body {
    padding: 0 !important;
    margin: 0 !important;
    background: transparent !important;
}

.ctc-common-list-wrap ::v-deep .employee-table-wrapper {
    overflow: visible !important;
    max-height: none !important;
}

.ctc-common-list-wrap ::v-deep .employee-list-table {
    width: 100%;
    margin-bottom: 0;
}

/* Header bar — 45px tall, 10px padding, cells divided by a lighter green */
.ctc-common-list-wrap ::v-deep .employee-list-table thead .table-head td {
    background-color: var(--ctc-green) !important;
    height: 45px;
    padding: 10px;
    border: 0 !important;
    text-align: center;
    vertical-align: middle;
}

/* Only the Descriptions column is divided off in the frame. */
.ctc-common-list-wrap ::v-deep .employee-list-table thead .table-head td:nth-child(2) {
    border-left: 1px solid var(--ctc-head-divider) !important;
}

.ctc-common-list-wrap ::v-deep .employee-list-table thead .table-head td label,
.ctc-common-list-wrap ::v-deep .employee-list-table thead .table-head td label span {
    color: #FFFFFF !important;
    font-family: 'Poppins', sans-serif;
    font-size: 16px;
    font-weight: 600;
    line-height: 14px;
    letter-spacing: -0.02em;
    margin-bottom: 0;
}

/* This breakdown is a static, ordered statement — the running subtotals only
   make sense in the order they are built — so DemoList's column sorting is
   switched off here: `pointer-events: none` swallows its sortBy() click and the
   two caret glyphs (::before / ::after on the header span, from custom.scss)
   are removed with it. */
.ctc-common-list-wrap ::v-deep .employee-list-table thead .table-head td label {
    cursor: default !important;
    pointer-events: none;
}

.ctc-common-list-wrap ::v-deep .employee-list-table thead .table-head td label span {
    cursor: default !important;
}

.ctc-common-list-wrap ::v-deep .employee-list-table thead .table-head td label span::before,
.ctc-common-list-wrap ::v-deep .employee-list-table thead .table-head td label span::after {
    content: none !important;
    display: none !important;
}

/* Body cells — 40px rows, 10px padding, hairline #E8E8E8 grid */
.ctc-common-list-wrap ::v-deep .employee-list-table tbody td {
    height: 40px;
    padding: 10px;
    font-family: 'Poppins', sans-serif;
    font-size: 15px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: -0.02em;
    color: var(--ctc-ink);
    vertical-align: middle;
    border: 1px solid var(--ctc-rule);
}

/* Column widths, matching the Other Perks table above: 310.92 / 236 / 184 / 187
   of the frame's 918px table. */
.ctc-common-list-wrap ::v-deep .employee-list-table tr > td:nth-child(1) { width: 33.87%; }
.ctc-common-list-wrap ::v-deep .employee-list-table tr > td:nth-child(2) { width: 25.71%; }
.ctc-common-list-wrap ::v-deep .employee-list-table tr > td:nth-child(3) { width: 20.04%; }
.ctc-common-list-wrap ::v-deep .employee-list-table tr > td:nth-child(4) { width: 20.38%; }

/* The money columns carry a formatted string ("৳3,00,000"), which DemoList does
   not recognise as numeric, so it leaves its own `text-left` on the cell — and
   that is Bootstrap's !important utility, hence the !important here to win it.
   The `.cursor-pointer` span inside is inline and inherits the alignment. */
.ctc-common-list-wrap ::v-deep .employee-list-table tbody td:nth-child(3),
.ctc-common-list-wrap ::v-deep .employee-list-table tbody td:nth-child(4) {
    text-align: right !important;
}

/* Column 5 carries the row kind (item / subtotal / total) purely so that
   DemoList's getValueClass() will stamp a class we can style rows by. It is
   never meant to be read, so it is hidden in both header and body. */
.ctc-common-list-wrap ::v-deep .employee-list-table tr > td:nth-child(5) {
    display: none !important;
}

/* Cost-type badges. DemoList turns the `status` column value into
   `status-label status-label-fixed pay` / `status-label-variable pay`. */
.ctc-common-list-wrap ::v-deep td.status-label {
    text-align: center;
}

.ctc-common-list-wrap ::v-deep td.status-label span {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 119px;
    height: 22px;
    min-width: 0;
    padding: 4.94px 8.24px;
    border-radius: 15px;
    border: 0.82px solid transparent;
    font-family: 'Poppins', sans-serif;
    font-size: 10px;
    font-weight: 500;
    line-height: 8px;
    letter-spacing: -0.02em;
    text-align: center;
    white-space: nowrap;
    cursor: default;
}

.ctc-common-list-wrap ::v-deep td.status-label-fixed span {
    background-color: var(--ctc-fixed-bg);
    border-color: var(--ctc-fixed-border) !important;
    color: var(--ctc-fixed-text);
}

.ctc-common-list-wrap ::v-deep td.status-label-variable span {
    background-color: var(--ctc-variable-bg);
    border-color: var(--ctc-variable-border) !important;
    color: var(--ctc-variable-text);
}

/* Subtotal and total rows, keyed off the hidden row-kind cell.
   :has() is required here — the styling hook sits in a later sibling cell than
   the ones being styled, which no other selector can reach. */
.ctc-common-list-wrap ::v-deep tr:has(td.status-label-subtotal) {
    background-color: var(--ctc-subtotal-bg);
}

.ctc-common-list-wrap ::v-deep tr:has(td.status-label-subtotal) td,
.ctc-common-list-wrap ::v-deep tr:has(td.status-label-subtotal) td span {
    font-weight: 600;
}

.ctc-common-list-wrap ::v-deep tr:has(td.status-label-total) {
    background-color: var(--ctc-total-bg);
}

.ctc-common-list-wrap ::v-deep tr:has(td.status-label-total) td,
.ctc-common-list-wrap ::v-deep tr:has(td.status-label-total) td span {
    font-weight: 600;
}

/* Rows are not clickable in this read-only breakdown. */
.ctc-common-list-wrap ::v-deep .employee-list-table tbody td,
.ctc-common-list-wrap ::v-deep .employee-list-table tbody td span.cursor-pointer {
    cursor: default !important;
}

.ctc-common-list-wrap ::v-deep .no-result-cell {
    font-size: 14px;
    color: var(--ctc-ink);
}

/* Cost-type pill — 119x22, radius 15px, per the Figma export. The Other Perks
   table only ever shows variable-pay items, so it uses that variant. */
.ctc-group-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 119px;
    height: 22px;
    padding: 4.94px 8.24px;
    border-radius: 15px;
    border: 0.82px solid transparent;
    font-family: 'Poppins', sans-serif;
    font-size: 10px;
    font-weight: 500;
    line-height: 8px;
    letter-spacing: -0.02em;
    white-space: nowrap;
}

.badge-ctc {
    background-color: #E1F2FF;
    border-color: #B5CBE2;
    color: #0060C9;
}

/* Premium self-contained modal */
.custom-modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    /* Plain semi-transparent overlay — no blur filter, matching the rest of the
       system's modal backdrops. */
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1050;
}

.custom-modal-content {
    width: 90%;
    max-width: 420px;
    background: rgba(255, 255, 255, 0.95);
    border: 1px solid rgba(255, 255, 255, 0.3) !important;
    transform: scale(1);
    animation: modal-scale 0.25s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}

@keyframes modal-scale {
    from {
        transform: scale(0.9);
        opacity: 0;
    }
    to {
        transform: scale(1);
        opacity: 1;
    }
}

/* Fade transition */
.fade-enter-active, .fade-leave-active {
    transition: opacity 0.2s ease;
}
.fade-enter, .fade-leave-to {
    opacity: 0;
}

/* Download-format buttons in the modal — PDF takes the same treatment as Fixed
   Report, Excel the same as Actual Report; only icon spacing is set here. */
.ctc-format-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

/* NOTE: the surrounding modal chrome (.modal-content/.modal-header/.modal-body)
   is an ancestor of this component's root, so scoped ::v-deep cannot reach it.
   Those overrides live in AnnualReport.vue under `.ctc-details-modal`. */
</style>
